/** @format */
import { useState, useEffect } from 'react';

/**
 * Server-Side Environment Detection Hook
 * Uses a smart configuration file with detection rules
 */
export const useSmartServerConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [environment, setEnvironment] = useState(null);

  useEffect(() => {
    // Load the smart configuration file
    fetch(`${process.env.PUBLIC_URL}/config.smart.json`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load smart config: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(smartConfig => {
        console.log('📋 Loaded smart configuration:', smartConfig);
        
        // Detect environment using server-defined rules
        const detectedEnv = detectEnvironmentFromRules(smartConfig.detection);
        setEnvironment(detectedEnv);
        
        // Get configuration for detected environment
        const envConfig = smartConfig.environments[detectedEnv];
        if (!envConfig) {
          throw new Error(`No configuration found for environment: ${detectedEnv}`);
        }
        
        console.log(`✅ Environment detected: ${detectedEnv}`);
        console.log('🎯 Using configuration:', envConfig);
        
        setConfig(envConfig);
        setLoading(false);
      })
      .catch(err => {
        console.error('❌ Failed to load smart configuration:', err);
        
        // Fallback to basic environment detection
        const fallbackEnv = detectBasicEnvironment();
        const fallbackConfig = getFallbackConfig(fallbackEnv);
        
        console.log(`🔄 Using fallback environment: ${fallbackEnv}`);
        console.log('🔄 Using fallback configuration:', fallbackConfig);
        
        setEnvironment(fallbackEnv);
        setConfig(fallbackConfig);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }, []);

  return { config, loading, error, environment };
};

/**
 * Detect environment using server-defined rules
 */
const detectEnvironmentFromRules = (detection) => {
  const { rules, default: defaultEnv } = detection;
  const location = window.location;
  
  console.log('🔍 Applying detection rules:', { rules, location: location.href });
  
  // Apply each rule in order
  for (const rule of rules) {
    const { condition, value, environment } = rule;
    
    switch (condition) {
      case 'hostname':
        if (location.hostname === value || location.hostname.includes(value)) {
          console.log(`✅ Rule matched - ${condition}: ${value} → ${environment}`);
          return environment;
        }
        break;
        
      case 'port':
        if (location.port === value) {
          console.log(`✅ Rule matched - ${condition}: ${value} → ${environment}`);
          return environment;
        }
        break;
        
      case 'protocol':
        if (location.protocol === value) {
          console.log(`✅ Rule matched - ${condition}: ${value} → ${environment}`);
          return environment;
        }
        break;
        
      case 'hostname_regex':
        const regex = new RegExp(value);
        if (regex.test(location.hostname)) {
          console.log(`✅ Rule matched - ${condition}: ${value} → ${environment}`);
          return environment;
        }
        break;
        
      case 'url_contains':
        if (location.href.includes(value)) {
          console.log(`✅ Rule matched - ${condition}: ${value} → ${environment}`);
          return environment;
        }
        break;
        
      default:
        console.warn(`⚠️ Unknown rule condition: ${condition}`);
    }
  }
  
  console.log(`🔄 No rules matched, using default: ${defaultEnv}`);
  return defaultEnv;
};

/**
 * Basic environment detection fallback
 */
const detectBasicEnvironment = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  }
  
  if (port === '8095' || port === '3000') {
    return 'development';
  }
  
  return 'production';
};

/**
 * Fallback configuration when smart config fails
 */
const getFallbackConfig = (environment) => {
  const configs = {
    development: {
      BASE_URL: 'http://localhost:8095/db/',
      COLLECTION_NAME: 'api/vstations',
      TILE_URL: '/map',
      IMAGE_URL: 'file:///app/images',
      COG_URL: 'cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png',
      GEOJSON_URL: 'http://localhost:1337/api/geojson',
      MAPBOX_TOKEN: 'pk.eyJ1IjoiYm1tb3JlaXJhIiwiYSI6ImNsMG16MmxvdTE3ZzEzZXB3aWZqNnd6azIifQ.ZOe3fv_Xwbr8TIm5L007yQ'
    },
    staging: {
      BASE_URL: 'https://staging-api.hydrologyfromspace.org/',
      COLLECTION_NAME: 'api/vstations',
      TILE_URL: 'https://staging-tile.hydrologyfromspace.org',
      IMAGE_URL: 'https://staging.hydrologyfromspace.org/images',
      COG_URL: 'cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png',
      GEOJSON_URL: 'https://staging.hydrologyfromspace.org/assets/data/geojson/vs_All.geojson',
      MAPBOX_TOKEN: 'pk.eyJ1IjoiYm1tb3JlaXJhIiwiYSI6ImNsMG16MmxvdTE3ZzEzZXB3aWZqNnd6azIifQ.ZOe3fv_Xwbr8TIm5L007yQ'
    },
    production: {
      BASE_URL: 'https://api.hydrologyfromspace.org',
      COLLECTION_NAME: 'api/vstations',
      TILE_URL: 'https://tile.hydrologyfromspace.org',
      IMAGE_URL: 'https://hydrologyfromspace.org/images',
      COG_URL: 'cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png',
      GEOJSON_URL: 'https://api.hydrologyfromspace.org/api/geojson',
      MAPBOX_TOKEN: 'pk.eyJ1IjoiYm1tb3JlaXJhIiwiYSI6ImNsMG16MmxvdTE3ZzEzZXB3aWZqNnd6azIifQ.ZOe3fv_Xwbr8TIm5L007yQ'
    }
  };
  
  return configs[environment] || configs.development;
};
